import { useState } from 'react'
import type { EditorState } from 'prosemirror-state'
import type { CellReference, TableEdit } from '../schemas'

export interface TableCellDraft {
  editorState: EditorState
  references: CellReference[]
  expectedValue: string
  expectedRevision?: string | null
  expectedQuestionRevision?: number
}

export interface TableCellDrafts {
  drafts: Record<string, Record<string, TableCellDraft>>
  change: (rowId: string, columnId: string, draft: TableCellDraft | null) => void
}

/** Workspace-owned edits survive virtual rows, pagination, and the Source Files tab. */
export function useTableCellDrafts() {
  const [drafts, setDrafts] = useState<Record<string, Record<string, TableCellDraft>>>({})
  const change = (rowId: string, columnId: string, draft: TableCellDraft | null) => {
    setDrafts((current) => {
      const row = { ...current[rowId] }
      if (draft) row[columnId] = draft
      else delete row[columnId]
      const next = { ...current }
      if (Object.keys(row).length) next[rowId] = row
      else delete next[rowId]
      return next
    })
  }
  const discardDeleted = (edit: TableEdit) => {
    if (edit.action !== 'delete-row' && edit.action !== 'delete-rows' && edit.action !== 'delete-column') return
    setDrafts((current) => {
      const next = { ...current }
      if (edit.action === 'delete-row') delete next[edit.row_id]
      else if (edit.action === 'delete-rows') {
        for (const rowId of edit.row_ids) delete next[rowId]
      } else {
        for (const [rowId, drafts] of Object.entries(current)) {
          const row = { ...drafts }
          delete row[edit.column_id]
          if (Object.keys(row).length) next[rowId] = row
          else delete next[rowId]
        }
      }
      return next
    })
  }
  return { drafts, change, discardDeleted }
}
